import type { ReactElement } from 'react';
import { Box, FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import {
  type AssistantAgentName,
  type AssistantConfig,
  type AssistantModel,
  DEFAULT_ASSISTANT_AGENT_NAME,
} from '@ardoq/api-types';
import { getDropdownMenuPortalTarget } from '@ardoq/dropdown-menu';
import { Select, type SelectOption, type SelectProps } from '@ardoq/select';
import { zIndex } from '@ardoq/design-tokens';
import { t, type I18nLocale } from '@ardoq/i18n';

const AGENT_OPTIONS: SelectOption<AssistantAgentName>[] = [
  { label: 'Assistant with tools', value: DEFAULT_ASSISTANT_AGENT_NAME },
  { label: 'Assistant with GraphLake', value: 'ardoq_assistant_graphlake' },
  { label: 'Unrestricted code agent', value: 'unrestricted_code_agent' },
];

const MODEL_OPTIONS: SelectOption<AssistantModel>[] = [
  { value: 'anthropic-claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
  { value: 'anthropic-claude-opus-4-6', label: 'Claude Opus 4.6' },
  { value: 'anthropic-claude-opus-5-5', label: 'Claude Opus 5.5' },
  { value: 'qwen3', label: 'Qwen3' },
];

const SELECT_STYLES = {
  menuPortal: base => ({ ...base, zIndex: zIndex.MODAL_BACKDROP + 1 }),
} satisfies Pick<NonNullable<SelectProps<string>['styles']>, 'menuPortal'>;

type ConfigSelectRowProps<T> = {
  label: string;
  options: SelectOption<T>[];
  value: T;
  onValueChange: (value: T | null) => void;
  isDisabled: boolean;
  dataTestId: string;
};

const ConfigSelectRow = <T,>({
  label,
  options,
  value,
  onValueChange,
  isDisabled,
  dataTestId,
}: ConfigSelectRowProps<T>): ReactElement => (
  <FlexBox align="center" gap="small">
    <Text variant="text2">{label}</Text>
    <Box flexGrow={1} flexShrink={1} minWidth="0">
      <Select
        aria-label={label}
        options={options}
        value={value}
        isDisabled={isDisabled}
        isSearchable={false}
        menuPlacement="top"
        menuPortalTarget={getDropdownMenuPortalTarget()}
        onValueChange={onValueChange}
        styles={SELECT_STYLES}
        dataTestId={dataTestId}
      />
    </Box>
  </FlexBox>
);

export type AssistantConfigSelectProps = {
  config: AssistantConfig;
  onConfigChange: (config: AssistantConfig) => void;
  i18nLocale: I18nLocale;
  isDisabled?: boolean;
};

export const AssistantConfigSelect = ({
  config,
  onConfigChange,
  i18nLocale,
  isDisabled = false,
}: AssistantConfigSelectProps): ReactElement => {
  const label = t(
    i18nLocale,
    'assistant:assistantConfig.agentSelectLabel',
    'Agent'
  );
  const modelLabel = t(
    i18nLocale,
    'assistant:assistantConfig.modelSelectLabel',
    'Model'
  );
  return (
    <FlexBox flexDirection="column" gap="small" width="full" marginTop="small">
      <ConfigSelectRow
        label={label}
        options={AGENT_OPTIONS}
        value={config.agent}
        onValueChange={agent => agent && onConfigChange({ ...config, agent })}
        isDisabled={isDisabled}
        dataTestId="assistant-config-agent-select"
      />
      <ConfigSelectRow
        label={modelLabel}
        options={MODEL_OPTIONS}
        value={config.model}
        onValueChange={model => model && onConfigChange({ ...config, model })}
        isDisabled={isDisabled}
        dataTestId="assistant-config-model-select"
      />
    </FlexBox>
  );
};
